/**
 * Translator actions — namespaced Vuex actions for the in-codebase
 * Translator plugin. Migrated from the standalone
 * efficient-vineyard-translator plugin. Data layer was rewired:
 *  - `post`        — Storage upload + Firestore datasets/{id} doc
 *                    (replaces the legacy api.efficientvineyard.com
 *                    /post/geojson endpoint).
 *  - `initPlugin`  — farm comes from rootState (no farm fetch).
 *  - map style     — Mapbox satellite (replaces the legacy MapTiler
 *                    style with the inline maptiler API key).
 */
import firebase from 'firebase/compat/app'
import { deriveHeaders } from '@/lib/deriveHeaders'
const fb = require('@/firebaseConfig.js')

export default {
  runTranslation ({ commit, state }) {
    commit('setStateProperty', { property: 'runningTranslation', value: true })
    commit('setStateProperty', { property: 'translationGeoJson', value: {} })
    const translationGeoJson = {
      type: 'FeatureCollection',
      features: []
    }
    state.datasetGeoJson.features.forEach(datasetFeature => {
      const translatedValue = (datasetFeature.properties[state.datasetHeader] - state.slopeIntercept.b) / state.slopeIntercept.m
      const translatedFeature = {
        type: 'Feature',
        properties: {},
        geometry: datasetFeature.geometry
      }
      translatedFeature.properties[state.samplesDatasetHeader + 'Translated'] = translatedValue
      translationGeoJson.features.push(translatedFeature)
    })
    setTimeout(() => {
      commit('setStateProperty', { property: 'translationGeoJson', value: translationGeoJson })
      commit('setStateProperty', { property: 'runningTranslation', value: false })
      commit('setStateProperty', { property: 'mode', value: 'save' })
    }, 3000)
  },
  async post ({ commit, state, rootState }) {
    commit('setStateProperty', { property: 'posting', value: true })
    try {
      const farmId = state.farm.id
      const path = `datasets/${farmId}/translation-${Date.now()}.geojson`
      await firebase.storage().ref(path).putString(
        JSON.stringify(state.translationGeoJson), 'raw',
        {
          contentType: 'application/json',
          // usageFinalize attributes bytes by customMetadata.userId —
          // without it, plugin-generated datasets aren't counted.
          customMetadata: { userId: (firebase.auth().currentUser && firebase.auth().currentUser.uid) || '' }
        }
      )
      const feats = (state.translationGeoJson && state.translationGeoJson.features) || []
      const headers = deriveHeaders({ features: feats })
      const uid = rootState.currentUser && rootState.currentUser.uid
      await fb.datasetsCollection.add({
        name: state.post.name,
        description: 'Generated with the Translator.',
        headers: headers,
        farmId: farmId,
        createdOn: new Date(),
        createdBy: uid,
        updatedOn: new Date(),
        updatedBy: uid,
        file: { geoJsonPath: path, path: path, originalFileName: 'translation.geojson' },
        folderId: (state.folder && state.folder.id) || 'root',
        dynamic: false,
        vizSettings: {
          colorHeader: state.samplesDatasetHeader + 'Translated',
          colorBy: 'valueBased',
          filters: []
        }
      })
      commit('setStateProperty', { property: 'posting', value: false })
      commit('setAlert', { active: true, message: 'This new dataset has been saved to your farm on Every.Farm!', level: 'alert-success', timeout: 4000 })
    } catch (err) {
      console.log(err)
      commit('setStateProperty', { property: 'posting', value: false })
      commit('setAlert', { active: true, message: 'There was an issue saving your translation to Every.Farm. Consider downloading the dataset directly and uploading manually.', level: 'alert-danger', timeout: 12000 })
    }
  },
  download ({ commit, state }) {
    let fileContents = JSON.stringify(state.translationGeoJson)
    fileContents = [fileContents]
    const blob = new Blob(fileContents, { type: 'text/plain;charset=utf-8' })
    const url = window.URL || window.webkitURL
    const link = url.createObjectURL(blob)
    const a = document.createElement('a')
    a.download = state.post.name + '.geojson'
    a.href = link
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  },
  reset ({ commit, state }) {
    if (state.mapObject.getLayer('datasetPreview')) {
      state.mapObject.removeLayer('datasetPreview')
      state.mapObject.removeSource('datasetPreview')
    }
    if (state.mapObject.getLayer('samplesDatasetPreview')) {
      state.mapObject.removeLayer('samplesDatasetPreview')
      state.mapObject.removeSource('samplesDatasetPreview')
    }
    if (state.mapObject.getLayer('samplesDatasetPreviewOutline')) {
      state.mapObject.removeLayer('samplesDatasetPreviewOutline')
      state.mapObject.removeSource('samplesDatasetPreviewOutline')
    }
    commit('setStateProperty', { property: 'dataset', value: {} })
    commit('setStateProperty', { property: 'samplesDataset', value: {} })
  },
  initTranslationMap ({ commit, state }) {
    // The Firestore GeoPoint may expose latitude/longitude getters or
    // the underscored backing fields depending on SDK version — handle
    // both like the Interpolator does.
    const g = state.farm.geolocation || {}
    const lat = g.latitude != null ? g.latitude : g._latitude
    const lng = g.longitude != null ? g.longitude : g._longitude
    mapboxgl.accessToken = 'pk.eyJ1IjoibGVyZ3AiLCJhIjoiY2p4bmI1NzNzMGN0MTNjcGx4cjF4eDBtdSJ9.2C0FEHhNZ-jGd7jgIRTrEQ' // eslint-disable-line
    state.mapObject = new mapboxgl.Map({ // eslint-disable-line
      container: 'translationMap',
      style: 'mapbox://styles/mapbox/satellite-v9',
      center: [lng != null ? lng : -95, lat != null ? lat : 40],
      zoom: 15
    })
    state.mapObject.addControl(new mapboxgl.NavigationControl()) // eslint-disable-line
  },
  initPlugin ({ commit, dispatch, rootState }) {
    // Farm comes from myEV's loaded state — no legacy API fetch.
    commit('setStateProperty', { property: 'spinner', value: true })
    commit('setStateProperty', { property: 'farm', value: rootState.farm })
    commit('setStateProperty', { property: 'spinner', value: false })
    dispatch('initTranslationMap')
  }
}
